import { useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { get } from '../api';
import { useBrand } from '../components/Brand';
import { contractorTypeOptions } from '../contractorTypes';
import { assignmentRef } from '../numbers';
import { dateLabel } from '../dates';
import { useLoad } from '../hooks';

interface Row {
  id: number;
  number: number;
  assignment_status: string;
  customer_name: string;
  client_name: string;
  lead_name: string | null;
  open_to_claim: boolean;
  charge_rate: string;
  charge_rate_unit: string;
  job_types: string[];
  start_date: string | null;
  end_date: string | null;
}

const PAGE = 50;
/** What the list shows when no status is chosen: the drafts still being finalised. */
const DEFAULT_STATUS = 'draft';
/** The "All" filter's own value, since an empty one means "the default". */
const ALL = 'all';
export const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  on_hold: 'On hold',
  scheduled: 'Scheduled',
  completed: 'Completed',
  cancelled: 'Cancelled',
};
/** The pill colour each status reads as, at a glance. */
export const STATUS_TONE: Record<string, string> = {
  draft: 'info',
  on_hold: 'warn',
  scheduled: 'ok',
  completed: 'fresh',
  cancelled: 'bad',
};

export function AssignmentsList() {
  const [params, setParams] = useSearchParams();
  const status = params.get('status') || DEFAULT_STATUS;
  const brand = useBrand();
  const serviceTypes = contractorTypeOptions(brand?.slug ?? '');
  const serviceType = (vs: string[] = []) =>
    vs.length > 0
      ? vs.map((v) => serviceTypes.find((t) => t.value === v)?.label ?? v).join(', ')
      : '—';
  const chips = useRef<HTMLDivElement>(null);
  // Keep the chosen filter in view when the row is scrolled (All sits at the far end).
  useEffect(() => {
    chips.current?.querySelector('.on')?.scrollIntoView?.({ inline: 'nearest', block: 'nearest' });
  }, [status]);
  const q = params.get('q') ?? '';
  const offset = Number(params.get('offset') ?? 0) || 0;

  const list = useLoad(() => {
    const qs = new URLSearchParams({ limit: String(PAGE), offset: String(offset) });
    if (status !== ALL) qs.set('status', status);
    if (q) qs.set('q', q);
    return get<{ items: Row[]; counts?: Record<string, number> }>(`/api/assignments?${qs}`);
  }, [status, q, offset]);
  // How many each status holds for this search. The last known counts stay up while
  // another filter loads, so the numbers do not blink.
  const lastCounts = useRef<Record<string, number> | null>(null);
  if (list.data?.counts) lastCounts.current = list.data.counts;
  const counts = lastCounts.current;
  const countOf = (k: string) =>
    k === ALL ? Object.values(counts ?? {}).reduce((a, b) => a + b, 0) : (counts?.[k] ?? 0);

  const change = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries({ offset: '', ...patch })) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next);
  };
  const search = (value: string) => value.trim() !== q && change({ q: value.trim() });
  const items = list.data?.items ?? [];

  return (
    <>
      <div className="row">
        <h1>Assignments</h1>
        <Link className="button" to="/assignments/new">
          Add assignment
        </Link>
      </div>
      <div className="filters" role="search">
        <label>
          Search customer, client or number
          <input
            type="search"
            defaultValue={q}
            onKeyDown={(e) => e.key === 'Enter' && search(e.currentTarget.value)}
            onBlur={(e) => search(e.currentTarget.value)}
          />
        </label>
      </div>
      <div ref={chips} className="chips filter-chips" role="group" aria-label="Status">
        {[...Object.entries(STATUS_LABELS), [ALL, 'All'] as const].map(([k, v]) => (
          <button
            key={k}
            type="button"
            className={k === status ? 'chip on' : 'chip'}
            aria-pressed={k === status}
            onClick={() => change({ status: k })}
          >
            {k === 'draft' ? 'Drafts' : v}
            {counts && (
              // Drafts are jobs still being finalised: their count stands out so none is forgotten.
              <span className={k === 'draft' && countOf(k) > 0 ? 'count' : 'count quiet'}>
                {countOf(k)}
              </span>
            )}
          </button>
        ))}
      </div>

      {list.error && (
        <p role="alert" className="error">
          {list.error}
        </p>
      )}
      {list.loading && <p>Loading…</p>}
      {!list.loading && !list.error && items.length === 0 && (
        <p className="empty">
          {q || status !== ALL ? 'No assignments match those filters.' : 'No assignments yet.'}
        </p>
      )}
      {items.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>No.</th>
              <th>Customer</th>
              <th>Client</th>
              <th>Service type</th>
              <th>Status</th>
              <th>Lead</th>
              <th>Charge</th>
              <th>Dates</th>
            </tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id}>
                <td className="record-number">{assignmentRef(a.number)}</td>
                <td>
                  <Link to={`/assignments/${a.id}`}>{a.customer_name || `#${a.id}`}</Link>
                </td>
                <td>{a.client_name}</td>
                <td>{serviceType(a.job_types)}</td>
                <td>
                  <span className={`pill ${STATUS_TONE[a.assignment_status] ?? 'info'}`}>
                    {STATUS_LABELS[a.assignment_status] ?? a.assignment_status}
                  </span>
                  {a.open_to_claim && !a.lead_name && <span className="muted"> Open to claim</span>}
                </td>
                <td>{a.lead_name ?? '—'}</td>
                {/* A draft may not have a charge or dates yet. */}
                <td>
                  {Number(a.charge_rate) > 0 ? `$${a.charge_rate} / ${a.charge_rate_unit}` : '—'}
                </td>
                <td>
                  {a.start_date
                    ? `${dateLabel(a.start_date)}${a.end_date ? ` to ${dateLabel(a.end_date)}` : ' onwards'}`
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {/* Only when there is another page to go to. */}
      {(offset > 0 || items.length >= PAGE) && (
        <div className="pager">
          <button
            type="button"
            disabled={offset === 0}
            onClick={() => change({ offset: String(Math.max(0, offset - PAGE) || ''), q })}
          >
            Previous
          </button>
          <button
            type="button"
            disabled={items.length < PAGE}
            onClick={() => change({ offset: String(offset + PAGE), q })}
          >
            Next
          </button>
        </div>
      )}
    </>
  );
}
